import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaSearch, FaLinkedin, FaGoogle, FaFilter, FaUsers, FaSortAmountDown, FaSortAmountDownAlt, FaDownload, FaChevronLeft, FaChevronRight, FaFilePdf } from 'react-icons/fa';
import { parseEducation, getAlmamater } from '../utils/education';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { generateAlumniCV } from '../utils/generateCV';
import usePageTitle from '../hooks/usePageTitle';

const ITEMS_PER_PAGE = 12;

const AlumniDirectory = () => {
  usePageTitle();
  const { isAdmin } = useAuth();
  const { t } = useLanguage();
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterJob, setFilterJob] = useState('');
  const [filterEdu, setFilterEdu] = useState('');
  const [filterPimnas, setFilterPimnas] = useState('');
  const [filterS2, setFilterS2] = useState('');
  const [filterS3, setFilterS3] = useState('');
  const [filterInstitution, setFilterInstitution] = useState('');
  const [filterResearch, setFilterResearch] = useState('');
  const [sortBy, setSortBy] = useState('terbaru');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    supabase.from('alumni').select('*').order('id', { ascending: false })
      .then(({ data }) => setAlumni(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const jobs = [...new Set(alumni.map(a => a.job).filter(Boolean))].sort();
  const educations = [...new Set(alumni.map(a => getAlmamater(a.education)).filter(Boolean))].sort();
  const pimnasYears = [...new Set(alumni.map(a => a.tahun_pimnas).filter(Boolean))].sort((a, b) => b - a);
  const s2List = [...new Set(alumni.map(a => parseEducation(a.education).s2_kampus).filter(Boolean))].sort();
  const s3List = [...new Set(alumni.map(a => parseEducation(a.education).s3_kampus).filter(Boolean))].sort();
  const institutions = [...new Set(alumni.map(a => a.institution).filter(Boolean))].sort();
  const researchList = [...new Set(alumni.flatMap(a => (a.research_interests || '').split(',').map(r => r.trim())).filter(Boolean))].sort();

  const filtered = alumni.filter(a => {
    const q = search.toLowerCase();
    const edu = parseEducation(a.education);
    const matchSearch = !q ||
      a.name?.toLowerCase().includes(q) ||
      a.email?.toLowerCase().includes(q) ||
      a.job?.toLowerCase().includes(q) ||
      a.institution?.toLowerCase().includes(q) ||
      a.research_interests?.toLowerCase().includes(q) ||
      getAlmamater(a.education).toLowerCase().includes(q) ||
      edu.s1_jurusan?.toLowerCase().includes(q) ||
      edu.s2_kampus?.toLowerCase().includes(q) ||
      edu.s2_jurusan?.toLowerCase().includes(q) ||
      edu.s3_kampus?.toLowerCase().includes(q) ||
      edu.s3_jurusan?.toLowerCase().includes(q) ||
      (a.tahun_pimnas && `pimnas ${a.tahun_pimnas}`.includes(q));
    const matchJob = !filterJob || a.job === filterJob;
    const matchEdu = !filterEdu || getAlmamater(a.education) === filterEdu;
    const matchPimnas = !filterPimnas || String(a.tahun_pimnas) === filterPimnas;
    const matchS2 = !filterS2 || (filterS2 === '__ANY__' ? !!edu.s2_kampus : edu.s2_kampus === filterS2);
    const matchS3 = !filterS3 || (filterS3 === '__ANY__' ? !!edu.s3_kampus : edu.s3_kampus === filterS3);
    const matchInstitution = !filterInstitution || a.institution === filterInstitution;
    const matchResearch = !filterResearch || (a.research_interests || '').split(',').map(r => r.trim()).includes(filterResearch);
    return matchSearch && matchJob && matchEdu && matchPimnas && matchS2 && matchS3 && matchInstitution && matchResearch;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'az': return (a.name || '').localeCompare(b.name || '');
      case 'za': return (b.name || '').localeCompare(a.name || '');
      case 'terlama': return (a.id || 0) - (b.id || 0);
      default: return (b.id || 0) - (a.id || 0); // terbaru
    }
  });

  // Pagination
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paged = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, filterJob, filterEdu, filterPimnas, filterS2, filterS3, filterInstitution, filterResearch, sortBy]);

  // Export CSV
  const exportCSV = () => {
    const headers = ['Nama', 'Email', 'Tahun PIMNAS', 'S1 Kampus', 'S1 Jurusan', 'S2 Kampus', 'S2 Jurusan', 'S3 Kampus', 'S3 Jurusan', 'Pekerjaan', 'Instansi', 'Minat Riset', 'LinkedIn', 'Google Scholar'];
    const rows = filtered.map(a => {
      const edu = parseEducation(a.education);
      return [a.name, a.email, a.tahun_pimnas, edu.s1_kampus, edu.s1_jurusan, edu.s2_kampus, edu.s2_jurusan, edu.s3_kampus, edu.s3_jurusan, a.job, a.institution, a.research_interests, a.linkedin, a.google_scholar];
    });
    const csvContent = [headers, ...rows].map(r => r.map(v => `"${(v || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `alumni-pimnas-${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = (id) => {
    if (!window.confirm(t('directory.confirmDelete'))) return;
    supabase.from('alumni').delete().eq('id', id)
      .then(({ error }) => {
        if (error) { alert('Gagal menghapus alumni'); return; }
        setAlumni(prev => prev.filter(a => a.id !== id));
      })
      .catch(() => alert('Gagal menghapus alumni'));
  };

  const clearFilters = () => { setFilterJob(''); setFilterEdu(''); setFilterPimnas(''); setFilterS2(''); setFilterS3(''); setFilterInstitution(''); setFilterResearch(''); setSortBy('terbaru'); setSearch(''); };

  const activeFilterCount = [filterJob, filterEdu, filterPimnas, filterS2, filterS3, filterInstitution, filterResearch].filter(Boolean).length + (sortBy !== 'terbaru' ? 1 : 0);

  return (
    <div className="font-inter bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-800 to-primary-700 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">{t('directory.title')}</h1>
          <p className="text-gray-300">{t('directory.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search & Filter Bar */}
        <div className="bg-white rounded-2xl shadow-md p-4 mb-8">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={t('directory.searchPlaceholder')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border transition font-medium text-sm relative ${showFilters ? 'bg-primary-50 border-primary-200 text-primary-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              <FaFilter /> {t('directory.filter')}
              {activeFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-primary-600 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{activeFilterCount}</span>
              )}
            </button>
            <Link
              to={isAdmin ? '/add-alumni' : '/ajukan-alumni'}
              className="inline-flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-600 text-white font-semibold px-5 py-3 rounded-xl transition shadow-md hover:shadow-lg text-sm"
            >
              {isAdmin ? t('directory.addAlumni') : t('directory.applyNow')}
            </Link>
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
              {/* Row 1: Sort & PIMNAS */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('directory.sortBy')}</label>
                  <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="terbaru">{t('directory.newest')}</option>
                    <option value="terlama">{t('directory.oldest')}</option>
                    <option value="az">{t('directory.nameAZ')}</option>
                    <option value="za">{t('directory.nameZA')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('directory.pimnasYear')}</label>
                  <select
                    value={filterPimnas}
                    onChange={e => setFilterPimnas(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">{t('directory.allYears')}</option>
                    {pimnasYears.map(y => <option key={y} value={y}>PIMNAS ke-{y}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('directory.job')}</label>
                  <select
                    value={filterJob}
                    onChange={e => setFilterJob(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">{t('directory.allJobs')}</option>
                    {jobs.map(j => <option key={j} value={j}>{j}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('directory.institution')}</label>
                  <select
                    value={filterInstitution}
                    onChange={e => setFilterInstitution(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">{t('directory.allInstitutions')}</option>
                    {institutions.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
              </div>
              {/* Row 2: Education */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('directory.almamaterS1')}</label>
                  <select
                    value={filterEdu}
                    onChange={e => setFilterEdu(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">{t('directory.allS1')}</option>
                    {educations.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('directory.campusS2')}</label>
                  <select
                    value={filterS2}
                    onChange={e => setFilterS2(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">{t('directory.allS2')}</option>
                    <option value="__ANY__">{t('directory.allWithS2')}</option>
                    {s2List.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('directory.campusS3')}</label>
                  <select
                    value={filterS3}
                    onChange={e => setFilterS3(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">{t('directory.allS3')}</option>
                    <option value="__ANY__">{t('directory.allWithS3')}</option>
                    {s3List.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('directory.researchInterest')}</label>
                  <select
                    value={filterResearch}
                    onChange={e => setFilterResearch(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">{t('directory.allResearch')}</option>
                    {researchList.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              {/* Reset */}
              {activeFilterCount > 0 && (
                <div className="flex items-center gap-3 pt-1">
                  <button onClick={clearFilters} className="text-sm text-red-500 hover:text-red-600 font-medium transition">
                    {t('directory.resetFilters')} ({activeFilterCount})
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Result Count & Export */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-gray-500 flex items-center gap-2">
            {sortBy === 'az' || sortBy === 'za' ? <FaSortAmountDownAlt className="text-gray-400" /> : <FaSortAmountDown className="text-gray-400" />}
            {t('directory.showing')} <span className="font-semibold text-gray-700">{paged.length}</span> {t('directory.of')} <span className="font-semibold text-gray-700">{filtered.length}</span> {t('directory.alumni')}
            {totalPages > 1 && <span className="text-gray-400">• {t('directory.page')} {page}/{totalPages}</span>}
            {sortBy !== 'terbaru' && <span className="text-primary-600 text-xs font-medium bg-primary-50 px-2 py-0.5 rounded-full">
              {sortBy === 'az' ? 'A → Z' : sortBy === 'za' ? 'Z → A' : 'Terlama'}
            </span>}
          </p>
          {isAdmin && filtered.length > 0 && (
            <button
              onClick={exportCSV}
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-4 py-2 rounded-xl transition"
            >
              <FaDownload /> {t('directory.exportCSV')}
            </button>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          </div>
        )}

        {/* Empty State */}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-20">
            <FaUsers className="text-5xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">{t('directory.noAlumniFound')}</h3>
            <p className="text-gray-400">{t('directory.tryChanging')}</p>
          </div>
        )}

        {/* Alumni Grid */}
        {!loading && filtered.length > 0 && (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {paged.map(al => {
              const edu = parseEducation(al.education);
              return (
              <div key={al.id} className="group bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 overflow-hidden transition-all transform hover:-translate-y-1">
                <div className="h-1.5 bg-gradient-to-r from-primary-500 to-gold-500"></div>

                <div className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    {al.photo_url ? (
                      <img src={al.photo_url} alt={al.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-gray-100" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-lg font-bold shrink-0">
                        {al.name?.charAt(0)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <Link to={`/alumni/${al.id}`} className="text-base font-semibold text-gray-900 hover:text-primary-600 transition-colors truncate block">
                        {al.name}
                      </Link>
                      <p className="text-sm text-gray-500 truncate">
                        {al.job}{al.job && al.institution ? ' · ' : ''}{al.institution || ''}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {al.tahun_pimnas && (
                      <span className="bg-gold-500/10 text-gold-600 text-xs font-semibold px-2.5 py-1 rounded-full">🏆 PIMNAS ke-{al.tahun_pimnas}</span>
                    )}
                    {edu.s1_kampus && (
                      <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-1 rounded-full" title="Almamater PIMNAS (S1)">🎓 {edu.s1_kampus}{edu.s1_jurusan ? ` — ${edu.s1_jurusan}` : ''}</span>
                    )}
                    {(edu.s2_kampus || edu.s2_jurusan) && (
                      <span className="bg-blue-50 text-blue-600 text-xs font-medium px-2.5 py-1 rounded-full">S2: {edu.s2_kampus || edu.s2_jurusan}</span>
                    )}
                    {(edu.s3_kampus || edu.s3_jurusan) && (
                      <span className="bg-purple-50 text-purple-600 text-xs font-medium px-2.5 py-1 rounded-full">S3: {edu.s3_kampus || edu.s3_jurusan}</span>
                    )}
                    {al.research_interests && al.research_interests.split(',').slice(0, 2).map((ri, i) => (
                      <span key={i} className="bg-emerald-50 text-emerald-600 text-xs font-medium px-2.5 py-1 rounded-full">{ri.trim()}</span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                    <div className="flex items-center gap-2">
                      {al.linkedin && (
                        <a href={al.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-600 transition-colors" title="LinkedIn">
                          <FaLinkedin className="text-lg" />
                        </a>
                      )}
                      {al.google_scholar && (
                        <a href={al.google_scholar} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-red-500 transition-colors" title="Google Scholar">
                          <FaGoogle className="text-lg" />
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => generateAlumniCV(al)}
                        className="text-xs font-medium text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg transition inline-flex items-center gap-1"
                        title="Download CV PDF"
                      >
                        <FaFilePdf /> CV
                      </button>
                      {isAdmin && (
                        <>
                          <Link to={`/edit-alumni/${al.id}`} className="text-xs font-medium text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition">
                            Edit
                          </Link>
                          <button
                            onClick={() => handleDelete(al.id)}
                            className="text-xs font-medium text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition"
                          >
                            {t('directory.delete')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FaChevronLeft className="text-xs" /> Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce((acc, p, i, arr) => {
                  if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === '...' ? (
                    <span key={`dots-${i}`} className="px-2 text-gray-400">...</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-10 h-10 rounded-xl text-sm font-medium transition ${
                        page === p
                          ? 'bg-primary-600 text-white shadow-md'
                          : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <FaChevronRight className="text-xs" />
              </button>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default AlumniDirectory;
